import {
  faDatabase,
  faFileExcel,
  faPlus,
  faX,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "../../global";

const BufferColumns = ({
  metadata,
  handleBufferColumnChange,
  removeBufferColumn,
  addBufferColumn,
  openExcelModal,
}) => {
  const fields = [
    "name",
    "target_column",
    "Item_Type",
    "from",
    "to",
    "rename_column",
  ];

  const formatLabel = (key) => {
    return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getFieldDescription = (key) => {
    const descriptions = {
      name: "Unique identifier for the buffer column",
      target_column: "Excel column to apply buffer to",
      Item_Type: "Type of item for categorization",
      from: "Starting Weight Break",
      to: "Ending Weight Break",
      rename_column: "New name for the column",
    };
    return descriptions[key] || "";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold" style={{ color: colors.primary }}>
          Buffer Columns
        </h3>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faDatabase}
            size={16}
            style={{ color: colors.darkGray }}
          />
          <span className="text-sm" style={{ color: colors.darkGray }}>
            {metadata.buffer_columns.length} buffer
            {metadata.buffer_columns.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Buffer Column List */}
      <div className="space-y-4">
        {metadata.buffer_columns.map((buffer, index) => (
          <div
            key={index}
            className="group relative bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-all duration-200"
          >
            {/* Column Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-semibold"
                  style={{ backgroundColor: colors.primary }}
                >
                  {index + 1}
                </div>
                <div>
                  <span
                    className="text-sm font-medium"
                    style={{ color: colors.primary }}
                  >
                    Buffer Column {index + 1}
                  </span>
                  {buffer.name && (
                    <p className="text-xs" style={{ color: colors.darkGray }}>
                      {buffer.name}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => removeBufferColumn(index)}
                className="opacity-0 group-hover:opacity-100 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 hover:bg-red-50"
                style={{ color: colors.error }}
                type="button"
                aria-label="Remove Buffer Column"
              >
                <FontAwesomeIcon icon={faX} size="16" />
              </button>
            </div>

            {/* Input Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {fields.map((key) => {
                const isTargetColumn = key === "target_column";

                return (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label
                        className="text-sm font-medium"
                        style={{ color: colors.darkGray }}
                      >
                        {formatLabel(key)}
                      </label>
                      {getFieldDescription(key) && (
                        <div
                          className="group/tooltip relative cursor-help"
                          title={getFieldDescription(key)}
                        >
                          <div
                            className="w-4 h-4 rounded-full flex items-center justify-center text-xs"
                            style={{
                              backgroundColor: colors.gray,
                              color: colors.darkGray,
                            }}
                          >
                            ?
                          </div>
                        </div>
                      )}
                    </div>

                    {isTargetColumn ? (
                      <div className="flex">
                        <input
                          type="text"
                          value={buffer[key] || ""}
                          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
                          style={{
                            "--tw-ring-color": colors.primary + "40",
                            borderColor: buffer[key]
                              ? colors.primary + "40"
                              : undefined,
                          }}
                          placeholder={`Enter ${formatLabel(
                            key
                          ).toLowerCase()}`}
                          onChange={(e) =>
                            handleBufferColumnChange(index, key, e.target.value)
                          }
                        />
                        {openExcelModal &&
                          (metadata.file_name.endsWith(".csv") ||
                            metadata?.sheet_information?.sheet_name) && (
                            <button
                              onClick={() => openExcelModal(index)}
                              type="button"
                              aria-label="Open Excel Sheet"
                              className="px-3 py-2 border border-l-0 border-gray-200 rounded-r-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
                              style={{ color: colors.primary }}
                            >
                              <FontAwesomeIcon icon={faFileExcel} size="16" />
                            </button>
                          )}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={buffer[key] || ""}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
                        style={{
                          "--tw-ring-color": colors.primary + "40",
                          borderColor: buffer[key]
                            ? colors.primary + "40"
                            : undefined,
                        }}
                        placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
                        onChange={(e) =>
                          handleBufferColumnChange(index, key, e.target.value)
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Progress Indicator */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div
                className="flex items-center justify-between text-xs"
                style={{ color: colors.darkGray }}
              >
                <span>Configuration Progress</span>
                <span>
                  {fields.filter((field) => buffer[field]).length}/
                  {fields.length} fields completed
                </span>
              </div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: colors.primary,
                    width: `${
                      (fields.filter((field) => buffer[field]).length /
                        fields.length) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Buffer Column Button */}
      <button
        onClick={addBufferColumn}
        className="w-full py-4 px-6 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-3 text-sm font-medium hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 group"
        style={{ color: colors.primary }}
        type="button"
      >
        <FontAwesomeIcon
          icon={faPlus}
          size={18}
          className="group-hover:scale-110 transition-transform duration-200"
        />
        Add Buffer Column
      </button>

      {/* Empty State */}
      {metadata.buffer_columns.length === 0 && (
        <div className="text-center py-12">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: colors.gray }}
          >
            <FontAwesomeIcon
              icon={faDatabase}
              size={24}
              style={{ color: colors.darkGray }}
            />
          </div>
          <h4
            className="text-lg font-semibold mb-2"
            style={{ color: colors.primary }}
          >
            No Buffer Columns Yet
          </h4>
          <p className="text-sm mb-4" style={{ color: colors.darkGray }}>
            Buffer columns help you manage data transformations and
            calculations.
          </p>
          <button
            onClick={addBufferColumn}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
            style={{
              backgroundColor: colors.primary,
              color: colors.white,
            }}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = colors.secondary)
            }
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = colors.primary)
            }
          >
            <FontAwesomeIcon icon={faPlus} size="16" />
            Create Your First Buffer Column
          </button>
        </div>
      )}

      {/* Info Section */}
      {metadata.buffer_columns.length > 0 && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ backgroundColor: colors.primary }}
          >
            <span className="text-white text-xs font-bold">💡</span>
          </div>
          <div className="space-y-1">
            <p
              className="text-sm font-medium"
              style={{ color: colors.primary }}
            >
              Buffer Column Tips
            </p>
            <ul
              className="text-xs space-y-1"
              style={{ color: colors.darkGray }}
            >
              <li>
                • Use descriptive names to identify your buffer columns easily
              </li>
              <li>
                • Target column should reference the exact Excel column
                letter/number
              </li>
              <li>• From/To values define the range for data processing</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default BufferColumns;
